import _ from "lodash";
/** External */
import React, { useId, useState } from "react";
import { connect } from "react-redux";

/** Internal */
import { useI18n } from "Hooks";
import { compose, graphql } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { Button, TextInput, FormInputError } from "@toddle-edu/ds-web";
import { FAMILY_INVITE_STATUS_INVITED } from "Constants/stringConstants";

/* Relative **/
import {
  setToastMsg,
  setChildStatus,
  getUserInfo,
} from "Login/modules/LoginModule";
import classes from "./AddChildPageV2.scss";
import Header from "./components/TopHeader";
import { goToRelativeRoute } from "modules/Services";
import { getChildrenQuery } from "modules/CommonQuery";
import { getChildrenFromCache } from "modules/CommonGraphqlHelpers";
import {
  getChildrenQueryVariables,
  getChildrenRefetchQueries,
  isExpiredToken,
} from "Platform/routes/FamilyProfile/modules/Utils";
import { addChild } from "Courses/modules/CoursesModule";
import PendingInvitationBannerV2 from "Teacher/routes/Courses/components/ParentHomePage/PendingInvitationBannerV2";

const AddChildPageV2 = props => {
  const {
    setChildStatus,
    children,
    pendingInvitations,
    setToastMsg,
    childrenQueryVariables,
    userId,
    userType,
    addChild,
    goToRelativeRoute,
  } = props;

  const { t } = useI18n();
  const inviteCodeErrorId = useId();

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [studentCode, setStudentCode] = useState("");

  // if children present then skip component render
  if (!_.isEmpty(children)) {
    setChildStatus(true);
    return null;
  }

  const handleInviteCode = e => {
    setStudentCode(e.target.value);
    setErrorMessage("");
  };

  const childrenRefetchQueries = getChildrenRefetchQueries({
    variables: childrenQueryVariables,
    userId,
    userType,
  });

  const handleAddChild = async () => {
    if (!studentCode) {
      setErrorMessage("Please enter invite code");
      return;
    }
    setShowFullScreenLoader(true);

    const result = await addChild({
      studentCode,
      userId,
      signInFlow: false,
      refetchQueries: childrenRefetchQueries,
    });

    if (!_.isEmpty(result)) {
      setToastMsg({ msg: "toastMsgs:invitation_accepted", type: "success" });
    }

    goToRelativeRoute({ route: "/", type: "replace" });

    setShowFullScreenLoader(false);
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleAddChild();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <Header />
      </div>
      <div className={classes.modalContent}>
        <div className={classes.contentContainer}>
          {pendingInvitations && _.size(pendingInvitations) ? (
            <div className={classes.banner}>
              <PendingInvitationBannerV2 shouldUpdateChildId="true" />
              <div className={classes.orContainer}>
                <div className={classes.orLineContainer} />
                <div className={`text-subtitle-1 ${classes.subtleText}`}>
                  {t("common:or_lowercase")}
                </div>
                <div className={classes.orLineContainer} />
              </div>
            </div>
          ) : null}
          <div className={classes.helperContainer}>
            <div className="text-label-1">{t("common:add_your_child")}</div>
            <div className={`text-body-l ${classes.subtleText}`}>
              {t("common:add_your_child_subtext_web")}
            </div>
          </div>
          <div className={classes.bottomContainer}>
            <div className={classes.inviteCodeContainer}>
              <div className={classes.inputContainer}>
                <TextInput
                  dsVersion="2.0"
                  label="Family invite code*"
                  size={"large"}
                  value={studentCode}
                  onChange={handleInviteCode}
                  onKeyPress={handleKeyPress}
                  autoFocus={_.size(pendingInvitations) === 0}
                  placeholder={t("common:enter_family_invite_code")}
                  aria-describedby={inviteCodeErrorId}
                />
              </div>
              <div className={classes.buttonContainer}>
                <Button
                  dsVersion="2.0"
                  variant="progressive"
                  size="large"
                  onClick={handleAddChild}
                  disabled={!studentCode}
                >
                  {t("common:continue")}
                </Button>
              </div>
            </div>
            <FormInputError id={inviteCodeErrorId} error={errorMessage} />
          </div>
        </div>
      </div>
      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo(state);
  const userId = _.get(userInfo, "id", "");
  const userType = _.get(userInfo, "user_type");
  const organizationId = _.get(userInfo, "org_id");

  const childrenQueryVariables = getChildrenQueryVariables({
    familyMemberId: userId,
    user_type: userType,
    organizationId,
    userId,
    includeInvitedStudents: true,
  });

  return {
    userId,
    userType,
    childrenQueryVariables,
    isData: true,
    isLoading: false,
  };
};

const mapActionCreators = {
  setChildStatus,
  addChild,
  setToastMsg,
  goToRelativeRoute,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getChildrenQuery, {
    name: "getChildren",
    options: ({ childrenQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: childrenQueryVariables,
    }),
    props({
      getChildren,
      ownProps: { childrenQueryVariables, isData, isLoading },
    }) {
      const childrenData = getChildrenFromCache(childrenQueryVariables);
      const childrenInfo = _.get(childrenData, "children", []);

      const pendingInvitations = _.filter(
        childrenInfo,
        childInfo =>
          _.get(childInfo, "parentStatus") === FAMILY_INVITE_STATUS_INVITED &&
          !isExpiredToken(_.get(childInfo, "inviteExpireTime"))[0]
      );

      const children = _.filter(
        childrenInfo,
        childInfo =>
          _.get(childInfo, "parentStatus") !== FAMILY_INVITE_STATUS_INVITED
      );

      return {
        isData: !_.isEmpty(childrenData) && isData,
        isLoading:
          _.includes([1, 2], getChildren["networkStatus"]) || isLoading,
        pendingInvitations,
        children,
      };
    },
  }),
  withLoader
)(AddChildPageV2);
